import React, { useState, useEffect } from 'react';
import { SunIcon, MoonIcon } from '@heroicons/react/24/outline';

// Try to import SuccessionThemeProvider, fall back gracefully if not available
let useSuccessionTheme;
try {
  const SuccessionThemeModule = require('./SuccessionThemeProvider');
  useSuccessionTheme = SuccessionThemeModule.useSuccessionTheme;
} catch (error) {
  // SuccessionThemeProvider not available, we'll handle this gracefully
  useSuccessionTheme = null;
}

const ThemeToggle = ({ className = '', size = 'default' }) => {
  // Try to use succession theme context if available
  let theme, toggleTheme, isLoading = false;
  
  if (useSuccessionTheme) {
    try {
      const themeContext = useSuccessionTheme();
      theme = themeContext.theme;
      toggleTheme = themeContext.toggleTheme;
      isLoading = !themeContext.isInitialized;
    } catch (error) {
      console.log('ThemeToggle: SuccessionThemeProvider not available, using fallback');
      // Fallback to local state management
      useSuccessionTheme = null;
    }
  }
  
  // Fallback state management when theme provider is not available
  const [localTheme, setLocalTheme] = useState('light');
  const [systemPreference, setSystemPreference] = useState('light');
  const [localIsLoading, setLocalIsLoading] = useState(true);

  // Use local state if theme provider is not available
  if (!useSuccessionTheme) {
    theme = localTheme;
    isLoading = localIsLoading;
    
    // Local toggle function
    toggleTheme = () => {
      const newTheme = localTheme === 'light' ? 'dark' : 'light';
      setLocalTheme(newTheme);
      localStorage.setItem('succession-theme', newTheme);
      applyThemeLocally(newTheme);

      // Dispatch custom event for cross-component synchronization
      window.dispatchEvent(new CustomEvent('succession-theme-change', {
        detail: { theme: newTheme }
      }));

      // Announce theme change to screen readers
      const announcement = `Theme switched to ${newTheme} mode`;
      announceToScreenReader(announcement);
    };
  }

  // Define size variants
  const sizeClasses = {
    small: 'w-8 h-8 p-1.5',
    default: 'w-10 h-10 p-2',
    large: 'w-12 h-12 p-2.5'
  };

  const iconSizeClasses = {
    small: 'w-4 h-4',
    default: 'w-5 h-5',
    large: 'w-6 h-6'
  };

  // Fallback: Detect system preference (only when theme provider not available)
  useEffect(() => {
    if (useSuccessionTheme) return; // Skip if using theme provider
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      setSystemPreference(e.matches ? 'dark' : 'light');
    };

    // Set initial system preference
    setSystemPreference(mediaQuery.matches ? 'dark' : 'light');

    // Listen for changes
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Fallback: Initialize theme from localStorage or system preference (only when theme provider not available)
  useEffect(() => {
    if (useSuccessionTheme) return; // Skip if using theme provider
    
    const savedTheme = localStorage.getItem('succession-theme');
    
    if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
      setLocalTheme(savedTheme);
      applyThemeLocally(savedTheme);
    } else {
      // No saved preference, use system preference
      setLocalTheme(systemPreference);
      applyThemeLocally(systemPreference);
    }
    
    setLocalIsLoading(false);
  }, [systemPreference]);

  // Fallback: Apply theme to document (only when theme provider not available)
  const applyThemeLocally = (newTheme) => {
    const root = document.documentElement;
    
    // Remove existing theme classes
    root.classList.remove('succession-light', 'succession-dark');
    
    // Add new theme class
    root.classList.add(`succession-${newTheme}`);
    
    // Add transition class for smooth switching
    root.classList.add('succession-theme-transition');
    
    // Add marketplace theme class if not already present
    if (!root.classList.contains('marketplace-theme')) {
      root.classList.add('marketplace-theme');
    }

    // Remove transition class after animation completes
    setTimeout(() => {
      root.classList.remove('succession-theme-transition');
    }, 300);
  };

  // Announce to screen readers
  const announceToScreenReader = (message) => {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    
    document.body.appendChild(announcement);
    
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleTheme();
    }
  };

  if (isLoading) {
    return (
      <div className={`${sizeClasses[size]} ${className} opacity-50 cursor-not-allowed`}>
        <div className="animate-spin rounded-full border-2 border-gray-300 dark:border-gray-600 border-t-gray-600 dark:border-t-gray-300 w-full h-full"></div>
      </div>
    );
  }

  return (
    <button
      onClick={toggleTheme}
      onKeyDown={handleKeyDown}
      className={`
        ${sizeClasses[size]} 
        ${className}
        relative flex items-center justify-center
        bg-succession-bg-secondary 
        border border-succession-border
        rounded-lg 
        transition-all duration-200 ease-in-out
        hover:bg-succession-hover-bg 
        hover:border-succession-border-hover
        hover:scale-105
        focus:outline-none 
        focus:ring-2 
        focus:ring-succession-focus-ring
        focus:ring-offset-2
        focus:ring-offset-succession-bg-primary
        active:scale-95
        cursor-pointer
        succession-focus-visible
      `}
      aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}
      aria-pressed={theme === 'dark'}
      role="switch"
      title={`Current theme: ${theme}. Click to switch to ${theme === 'light' ? 'dark' : 'light'} theme.`}
    >
      {/* Icon container with smooth transition */}
      <div className="relative">
        {/* Sun icon */}
        <SunIcon 
          className={`
            ${iconSizeClasses[size]}
            text-succession-text-primary
            transition-all duration-300 ease-in-out
            ${theme === 'light' 
              ? 'opacity-100 scale-100 rotate-0' 
              : 'opacity-0 scale-75 rotate-180 absolute inset-0'
            }
          `}
        />
        
        {/* Moon icon */}
        <MoonIcon 
          className={`
            ${iconSizeClasses[size]}
            text-succession-text-primary
            transition-all duration-300 ease-in-out
            ${theme === 'dark' 
              ? 'opacity-100 scale-100 rotate-0' 
              : 'opacity-0 scale-75 -rotate-180 absolute inset-0'
            }
          `}
        />
      </div>

      {/* Visual indicator dot */}
      <div 
        className={`
          absolute -top-1 -right-1 w-2 h-2 rounded-full
          transition-all duration-200 ease-in-out
          ${theme === 'dark' 
            ? 'bg-succession-primary scale-100' 
            : 'bg-transparent scale-0'
          }
        `}
      />
    </button>
  );
};

export default ThemeToggle; 